/**
 * Lecture et validation de la configuration d'environnement.
 *
 * Tout le paramétrage du front tient dans trois variables (voir
 * `.env.example`). Elles sont lues **une seule fois**, au chargement du
 * module, et validées immédiatement : une clé absente ou malformée est une
 * erreur de configuration, pas une erreur d'exécution à rattraper page par
 * page. `App` affiche alors un écran explicite plutôt que de laisser chaque
 * appel échouer en `401 invalid_site_key`.
 */

/** Environnement porté par la clé de site, déduit de son préfixe. */
export type SiteEnvironment = 'test' | 'live';

export type AppConfig = {
    /** Clé publique du site : `site_test_…` ou `site_live_…`. */
    siteKey: string;
    /** Base de l'API, sans « /api/v1 » — le SDK ajoute le préfixe. */
    apiUrl: string;
    /** Déduit du préfixe de la clé, avant même le premier appel réseau. */
    environment: SiteEnvironment;
    /** Affiche ou non le tiroir « Inspecteur d'API ». */
    showApiInspector: boolean;
};

/** Détail d'une configuration invalide, présenté à l'écran par `App`. */
export type ConfigError = {
    variable: string;
    problem: string;
    fix: string;
};

const rawSiteKey = (import.meta.env.VITE_DASHLITE_SITE_KEY ?? '').trim();
const rawApiUrl = (import.meta.env.VITE_DASHLITE_API_URL ?? '').trim();
const rawInspector = (import.meta.env.VITE_SHOW_API_INSPECTOR ?? 'true').trim();

/**
 * Valide les variables d'environnement et retourne la liste des problèmes.
 * Une liste vide signifie que `appConfig` est exploitable.
 */
function collectConfigErrors(): ConfigError[] {
    const errors: ConfigError[] = [];

    if (rawSiteKey === '' || rawSiteKey === 'site_test_remplacez_par_votre_cle') {
        errors.push({
            variable: 'VITE_DASHLITE_SITE_KEY',
            problem: "La clé de site n'est pas renseignée.",
            fix: 'Copiez `.env.example` en `.env` et collez la clé de test de votre site, telle qu\'affichée dans la console Dash Lite.',
        });
    } else if (!/^site_(test|live)_[A-Za-z0-9]{8,}$/.test(rawSiteKey)) {
        errors.push({
            variable: 'VITE_DASHLITE_SITE_KEY',
            problem: `La clé « ${maskKey(rawSiteKey)} » n'a pas la forme attendue.`,
            fix: 'Une clé de site commence toujours par `site_test_` ou `site_live_`, suivi d\'une suite alphanumérique. Vérifiez qu\'aucun espace ni guillemet ne s\'est glissé dans la valeur.',
        });
    }

    if (rawApiUrl === '') {
        errors.push({
            variable: 'VITE_DASHLITE_API_URL',
            problem: "L'adresse de l'API n'est pas renseignée.",
            fix: 'Renseignez `https://dash-lite.com` en production, ou l\'adresse de votre instance locale.',
        });
    } else if (!/^https?:\/\//.test(rawApiUrl)) {
        errors.push({
            variable: 'VITE_DASHLITE_API_URL',
            problem: `« ${rawApiUrl} » n'est pas une URL absolue.`,
            fix: 'Indiquez le schéma complet, par exemple `https://dash-lite.com`.',
        });
    } else if (rawApiUrl.includes('/api/v1')) {
        errors.push({
            variable: 'VITE_DASHLITE_API_URL',
            problem: "L'adresse contient déjà « /api/v1 ».",
            fix: 'Le SDK ajoute lui-même le préfixe de version : renseignez uniquement la base, par exemple `https://dash-lite.com`.',
        });
    }

    return errors;
}

/** Masque une clé pour un affichage sans fuite intégrale dans une capture. */
export function maskKey(key: string): string {
    if (key.length <= 16) {
        return key;
    }

    return `${key.slice(0, 12)}…${key.slice(-4)}`;
}

export const configErrors: ConfigError[] = collectConfigErrors();

export const appConfig: AppConfig = {
    siteKey: rawSiteKey,
    // La barre finale est retirée : le SDK concatène « /api/v1 » derrière.
    apiUrl: rawApiUrl.replace(/\/+$/, ''),
    environment: rawSiteKey.startsWith('site_live_') ? 'live' : 'test',
    showApiInspector: rawInspector !== 'false' && rawInspector !== '0',
};

/**
 * Adresse publique du site, utilisée pour construire les URL de retour
 * envoyées à l'API (`return_url` du portail de facturation, lien de
 * réinitialisation de mot de passe…).
 *
 * Ces URL doivent appartenir aux domaines déclarés pour le site, sans quoi
 * l'API répond `422 invalid_redirect_url`. On repart donc de l'origine réelle
 * du navigateur plutôt que d'une constante à maintenir.
 */
export function siteOrigin(): string {
    return window.location.origin;
}
